import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { initSentry } from "./sentry.config";

// Initialize Sentry before anything else
initSentry();
import { client } from "./networking/graphql";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SignInUpContainer } from "./containers/Login/LoginContainer";
import { PasswordResetContainer } from "./containers/Login/PasswordResetContainer";
import RegisterContainer from "./containers/Login/RegisterContainer";
import AppProvider from "./context/AppContext";
import PrivacyPolicy from "./containers/PrivacyPolicy";
import UserAgreement from "./containers/TermsOfUse";
import { UserWizardContainer } from "./containers/UserWizard/UserWizardContainer";
import { SYSTEM_USER_TYPES } from "./containers/UserWizard/types";
import { getCurrentPersona } from "./utils/persona";
import { ApplicationContextProvider } from "./context/ApplicationContext";
import { ApplicationLayout } from "./layouts/ApplicationLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { BrandingProvider } from "./context/BrandingContext";

// agbase-ui logo assets
import agbaseLogo from "./components/NavBar/agbase-new.png";

// Import route modules for code splitting by persona
import { fundingKitRoutes, FKUpgradeRoute } from "./routes/fundingKitRoutes";
import { portfolioIntelligenceRoutes } from "./routes/portfolioIntelligenceRoutes";
import { sharedRoutes } from "./routes/sharedRoutes";
import { lazy } from "react";

const SearchTestPage = lazy(() =>
  import("./containers/Search/SearchTestPage").then((m) => ({
    default: m.SearchTestPage,
  }))
);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

// Route definitions for FOUNDER persona
// Combines FundingKit-specific routes with shared routes
const founderRoutes = [...fundingKitRoutes];

// Route definitions for other personas (INVESTOR, ACCELERATOR, ACADEMIA, DFI_GOVT)
// Combines Portfolio Intelligence routes with shared routes
const personaRoutes = [...portfolioIntelligenceRoutes, ...sharedRoutes];

const AgBase = () => {
  const { session, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (window.location.hostname === "agbase.preview.briterbridges.com") {
      const currentUrl = new URL(window.location.href);
      const targetUrl = `https://intelligence.agbase.briter.co${currentUrl.pathname}${currentUrl.search}`;
      window.location.replace(targetUrl); // `replace` avoids back button loop
    }
  }, []);

  // Not authenticated - show auth + public routes
  if (!session?.id) {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInUpContainer />} />
        <Route path="/sign-up" element={<RegisterContainer />} />
        <Route path="/password-reset" element={<PasswordResetContainer />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<UserAgreement />} />
        <Route path="/onboarding" element={<UserWizardContainer />} />
        <Route
          path="*"
          element={
            <Navigate to={`/sign-in` + location.search} replace={false} />
          }
        />
      </Routes>
    );
  }

  // Authenticated - determine which route set to show based on persona
  const currentPersona = getCurrentPersona(user);
  const isFounder = currentPersona === SYSTEM_USER_TYPES.FOUNDER;
  const routes = isFounder ? founderRoutes : personaRoutes;

  return (
    <Routes>
      {/* Standalone routes (no layout) */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<UserAgreement />} />
      <Route path="/onboarding" element={<UserWizardContainer />} />

      {currentPersona === SYSTEM_USER_TYPES.FOUNDER && (
        <Route path={FKUpgradeRoute.path} element={FKUpgradeRoute.element} />
      )}
      {/* Layout route with ApplicationContext and ApplicationLayout */}
      <Route
        element={
          <ApplicationContextProvider persona={currentPersona as any}>
            <ApplicationLayout>
              <Outlet />
            </ApplicationLayout>
          </ApplicationContextProvider>
        }
      >
        {routes.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={<ProtectedRoute>{r.element}</ProtectedRoute>}
          />
        ))}
        {/* Search test page — available to all personas */}
        <Route
          path="/search"
          element={<ProtectedRoute><SearchTestPage /></ProtectedRoute>}
        />
      </Route>

      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

root.render(
  <React.StrictMode>
    <BrandingProvider
      value={{
        logoSrc: agbaseLogo,
        logoAlt: "AgBase by Briter",
        appName: "AgBase",
        orgBucket: "agbase_orgs",
      }}
    >
      <AppProvider>
        <ApolloProvider client={client}>
          <BrowserRouter>
            <AuthProvider>
              <AgBase />
            </AuthProvider>
          </BrowserRouter>
        </ApolloProvider>
      </AppProvider>
    </BrandingProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
